import React, { useRef, useCallback, useMemo } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextMetrics } from "pixi.js";
import { useTranslation } from "react-i18next";
import { createButtonPressAnimation } from "../../../../utilities";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import { FONT_CONFIG } from "../../../../textureConfigs";

const Checkbox = ({
  label,
  translationKey,
  checked = false,
  onChange,
  x = 0,
  y = 0,
  sizeMultiplier = 0.7, // Same as close button in settings
  labelFontSize, // Font size passed from parent
  labelStyle, // Optional: full text style passed from parent
  containerWidth, // Optional: max width available for label text
}) => {
  const { t } = useTranslation();
  const { getResponsiveConfig } = useDimensions();

  // Use translation if translationKey is provided, otherwise use label prop
  const displayLabel = translationKey ? t(translationKey) : label;

  // Refs for sprites
  const backgroundSpriteRef = useRef(null);
  const checkmarkSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(1.3, false);

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Get the checkbox textures
  const backgroundTexture = PIXI.Assets.get(
    checked
      ? ASSET_NAME.checkbox_background_active
      : ASSET_NAME.checkbox_background
  );
  const checkmarkTexture = PIXI.Assets.get(ASSET_NAME.checkmark);

  // Get responsive configuration from context (same as close button)
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Apply size multiplier to checkbox dimensions
  const checkboxSize = buttonConfig.width * sizeMultiplier;

  // Calculate label position (to the right of checkbox with some padding)
  const labelX = Math.round(checkboxSize * 0.75);
  const labelY = 0;

  // Auto-scaling font size calculation for label
  const finalLabelFontSize = useMemo(() => {
    const idealFontSize = labelFontSize || 26;

    // If no containerWidth provided, use ideal size
    if (!containerWidth) {
      return idealFontSize;
    }

    // Calculate available width for text (container width minus checkbox and padding)
    const maxTextWidth = containerWidth - labelX;

    // Measure text with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
      align: "left",
      wordWrap: true,
      wordWrapWidth: maxTextWidth,
    });
    const metrics = TextMetrics.measureText(displayLabel, tempStyle);

    // Scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    return idealFontSize;
  }, [labelFontSize, containerWidth, labelX, displayLabel]);

  // Create default label style if not provided
  const defaultLabelStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalLabelFontSize,
        fontWeight: "400",
        fill: 0xffffff, // White color
        align: "left",
        wordWrap: true,
        wordWrapWidth: containerWidth ? containerWidth - labelX : undefined,
      }),
    [finalLabelFontSize, containerWidth, labelX]
  );

  // Use provided labelStyle or default
  const textStyle = labelStyle || defaultLabelStyle;

  const handleClick = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);

    // Add press animation to background
    if (backgroundSpriteRef.current) {
      createButtonPressAnimation(backgroundSpriteRef.current);
    }

    // Call onChange callback
    if (onChange) {
      onChange(!checked);
    }
  }, [checked, onChange]);

  // Hover effect handlers
  const handlePointerOver = useCallback(() => {
    if (backgroundSpriteRef.current) {
      isHoveredRef.current = true;
      // Apply brightness filter
      if (
        !backgroundSpriteRef.current.filters ||
        backgroundSpriteRef.current.filters.length === 0
      ) {
        backgroundSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, []);

  const handlePointerOut = useCallback(() => {
    if (backgroundSpriteRef.current) {
      isHoveredRef.current = false;
      // Remove brightness filter
      backgroundSpriteRef.current.filters = [];
    }
  }, []);

  // Don't render if textures not loaded
  if (!backgroundTexture || !checkmarkTexture) {
    return null;
  }

  return (
    <Container
      name="secondary_checkbox"
      x={x}
      y={y}
      eventMode="static"
      cursor="pointer"
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
    >
      {/* Checkbox Background */}
      <Sprite
        ref={backgroundSpriteRef}
        texture={backgroundTexture}
        name="secondary_checkbox_background"
        anchor={0.5}
        x={0}
        y={0}
        width={checkboxSize}
        height={checkboxSize}
      />

      {/* Checkmark (only visible when checked) */}
      {checked && (
        <Sprite
          ref={checkmarkSpriteRef}
          texture={checkmarkTexture}
          name="secondary_checkmark"
          anchor={0.5}
          x={0}
          y={0}
          width={checkboxSize * 0.65} // Wider
          height={checkboxSize * 0.45} // Less tall - makes it more horizontal
          eventMode="none" // Don't intercept events
        />
      )}

      {/* Label Text */}
      <Text
        text={displayLabel}
        style={textStyle}
        anchor={{ x: 0, y: 0.5 }}
        x={labelX}
        y={labelY}
        resolution={2}
        alpha={0.85}
        eventMode="none" // Don't intercept events
      />
    </Container>
  );
};

export default Checkbox;
